import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, GraduationCap, Award, MapPin } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext.jsx';
import { fetchExpertById } from '../../utils/api/expertApi.js';
import { getImageUrl } from '../../utils/imageUtils.js';
import './ExpertDetail.css';

const labels = {
  ne: {
    back: 'फिर्ता जानुहोस्',
    education: 'शिक्षा',
    year: 'उत्कृष्टता वर्ष',
    address: 'ठेगाना',
    additional: 'थप जानकारी',
    notFound: 'विशेषज्ञ भेटिएन।',
    loading: 'लोड हुँदैछ…',
  },
  en: {
    back: 'Back',
    education: 'Education',
    year: 'Year of Excellence',
    address: 'Address',
    additional: 'Additional Information',
    notFound: 'Expert not found.',
    loading: 'Loading…',
  },
};

const ExpertDetailPage = () => {
  const { id } = useParams();
  const { language } = useLanguage();
  const t = labels[language] || labels.ne;
  const [expert, setExpert] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    fetchExpertById(id)
      .then((data) => {
        if (!mounted) return;
        setExpert(data?.data ?? data);
        setLoading(false);
      })
      .catch(() => {
        if (!mounted) return;
        setLoading(false);
      });
    return () => { mounted = false; };
  }, [id]);

  if (loading) {
    return (
      <main className="expert-detail page-enter">
        <div className="container">
          <p>{t.loading}</p>
        </div>
      </main>
    );
  }

  if (!expert) {
    return (
      <main className="expert-detail page-enter">
        <div className="container">
          <p>{t.notFound}</p>
          <Link to="/sectors" className="btn btn-primary">{t.back}</Link>
        </div>
      </main>
    );
  }

  return (
    <main className="expert-detail page-enter">
      <div className="container">
        <button onClick={() => window.history.back()} className="expert-detail__back">
          <ArrowLeft size={16} />
          {t.back}
        </button>

        <div className="expert-detail__card">
          <div className="expert-detail__avatar">
            {expert.photoUrl ? (
              <img src={getImageUrl(expert.photoUrl)} alt={expert.name} />
            ) : (
              <span className="expert-detail__avatar-fallback">
                {expert.name ? expert.name.charAt(0).toUpperCase() : '?'}
              </span>
            )}
          </div>

          <h1 className="expert-detail__name">{expert.name}</h1>
          {expert.sector && <p className="expert-detail__sector">{expert.sector}</p>}

          <div className="expert-detail__meta-grid">
            {expert.education && (
              <div className="expert-detail__meta-item">
                <GraduationCap size={18} />
                <div>
                  <span className="expert-detail__meta-label">{t.education}</span>
                  <span className="expert-detail__meta-value">{expert.education}</span>
                </div>
              </div>
            )}
            {expert.yearOfExcellence && (
              <div className="expert-detail__meta-item">
                <Award size={18} />
                <div>
                  <span className="expert-detail__meta-label">{t.year}</span>
                  <span className="expert-detail__meta-value">{expert.yearOfExcellence}</span>
                </div>
              </div>
            )}
            {expert.address && (
              <div className="expert-detail__meta-item">
                <MapPin size={18} />
                <div>
                  <span className="expert-detail__meta-label">{t.address}</span>
                  <span className="expert-detail__meta-value">{expert.address}</span>
                </div>
              </div>
            )}
          </div>

          {expert.additionalInfo && (
            <div className="expert-detail__additional">
              <h2>{t.additional}</h2>
              <p>{expert.additionalInfo}</p>
            </div>
          )}
        </div>
      </div>
    </main>
  );
};

export default ExpertDetailPage;
